import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, ExternalLink, Pencil, Copy, Check } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import type { Project } from "@shared/schema";

/** A duplikálás válasza: a másolat + mit vitt magával. */
type DuplicateResult = {
  project: Project;
  unitCount: number;
  floorCount: number;
  apartmentCount: number;
};

export function AdminProjectsList() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["admin", "projects"],
    queryFn: () => api<Project[]>("/api/admin/projects"),
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/projects/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "projects"] }),
  });

  // Duplikálás: a szerver egy tranzakcióban klónozza a teljes projektfát
  // (projekt → lakások → emeletek → zónák), így a nagy galéria- és
  // alaprajz-listákat nem kell a kliensen átküldeni. A másolat piszkozatként
  // jön létre, ezt a visszajelzés is kimondja.
  const [dupMessage, setDupMessage] = useState<string | null>(null);
  const [dupError, setDupError] = useState<string | null>(null);
  useEffect(() => {
    if (!dupMessage) return;
    const t = setTimeout(() => setDupMessage(null), 6000);
    return () => clearTimeout(t);
  }, [dupMessage]);

  const duplicate = useMutation({
    mutationFn: (id: string) =>
      api<DuplicateResult>(`/api/admin/projects/${id}/duplicate`, {
        method: "POST",
      }),
    onSuccess: (r) => {
      setDupError(null);
      const parts = [`${r.unitCount} lakás`];
      if (r.floorCount > 0) {
        parts.push(`${r.floorCount} emelet`, `${r.apartmentCount} zóna`);
      }
      setDupMessage(
        `Projekt duplikálva: ${r.project.name} (${parts.join(", ")}). ` +
          "A másolat piszkozat — publikálni a szerkesztőben lehet.",
      );
      qc.invalidateQueries({ queryKey: ["admin", "projects"] });
    },
    onError: (err) =>
      setDupError(
        err instanceof ApiError
          ? `Nem sikerült a duplikálás: ${err.message}`
          : "Nem sikerült a duplikálás.",
      ),
  });

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("admin.projects.title")}</h1>
        {/* Inside the nested /admin router, hrefs resolve relative to the
            base — an absolute /admin/... here would become /admin/admin/... */}
        <Link href="/projects/new" className="btn-primary">
          <Plus size={16} /> {t("admin.projects.new")}
        </Link>
      </div>

      {dupMessage && (
        <div className="mt-4 inline-flex items-start gap-1.5 text-sm text-green-600">
          <Check size={15} className="mt-0.5 shrink-0" /> {dupMessage}
        </div>
      )}
      {dupError && <div className="mt-4 text-sm text-red-600">{dupError}</div>}

      <div className="mt-6 card overflow-hidden">
        {q.isLoading && (
          <div className="p-8 text-brand-500">{t("common.loading")}</div>
        )}
        {q.data && q.data.length === 0 && (
          <div className="p-8 text-brand-500">{t("admin.projects.noProjects")}</div>
        )}
        {q.data && q.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-brand-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Név</th>
                <th className="text-left px-4 py-3">Slug</th>
                <th className="text-left px-4 py-3">Státusz</th>
                <th className="text-left px-4 py-3">Publikálva</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((p) => (
                <tr key={p.id} className="border-t border-brand-200">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      href={`/projects/${p.id}`}
                      className="hover:text-brand-700"
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-brand-500 font-mono text-xs">
                    {p.slug}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs bg-brand-100 text-brand-700 px-2 py-1 rounded">
                      {t(`projects.statusLabel.${p.status}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {p.isPublished ? (
                      <span className="text-xs text-emerald-700">Igen</span>
                    ) : (
                      <span className="text-xs text-brand-400">Nem</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                    <Link
                      href={`/projects/${p.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white bg-accent hover:bg-accent-700 px-3 py-1.5 rounded transition-colors"
                      title="Szerkesztés"
                    >
                      <Pencil size={13} /> Szerkesztés
                    </Link>
                    <button
                      type="button"
                      disabled={duplicate.isPending}
                      onClick={() => {
                        setDupMessage(null);
                        duplicate.mutate(p.id);
                      }}
                      className="text-brand-500 hover:text-brand-900 disabled:opacity-40 inline-block align-middle"
                      title="Duplikálás — a lakásokkal, emeletekkel és zónákkal együtt"
                    >
                      <Copy size={16} />
                    </button>
                    <a
                      href={`/projektek/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-500 hover:text-brand-900 inline-block align-middle"
                      title="Megnyitás a weboldalon"
                    >
                      <ExternalLink size={16} />
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Biztosan törli: ${p.name}?`)) {
                          del.mutate(p.id);
                        }
                      }}
                      className="text-red-500 hover:text-red-700"
                      title="Törlés"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
